// =========================================================================
// Reports services (read-only).
//
// Thin TypeScript wrappers around the SECURITY DEFINER RPCs introduced in
// supabase/migrations/20260628030000_reports_init.sql and amended in
// 20260628050000_reports_revenue_accuracy.sql (revenue is now NET OF
// SALES TAX, with refunds surfaced as a contra-revenue line bucketed on
// orders.payments.refunded_at). The underlying functions enforce auth +
// org-access checks; these wrappers only shape arguments and return
// types.
//
// All revenue / expense amounts are bigint cents.
// =========================================================================

import { supabase } from '../client';
import { requireOrgContext, type ActiveContext } from '../activeContext';
import {
  buildNextCursor,
  clampPageSize,
  type KeysetInput,
  type ListResult,
} from '../pagination';

// ---- types --------------------------------------------------------------

export type ReportsBasis = 'accrual' | 'cash';
export type ReportsChannel = 'clients' | 'wholesalers';
export type ReportsExpenseKind = 'commissions' | 'shipping_labels' | 'sms';
export type ReportsTimeseriesGrain = 'day' | 'week' | 'month';

export interface ReportsRangeInput {
  /** Inclusive lower bound. Pass an ISO timestamp; null defers to the SQL default (now() - 30 days). */
  from?: string | null;
  /** Exclusive upper bound. Pass an ISO timestamp; null defers to now(). */
  to?: string | null;
}

export interface ReportsScopeInput extends ReportsRangeInput {
  /** Optional store narrowing. `null` (or omitted) means org-wide across all stores. */
  storeId?: string | null;
  basis?: ReportsBasis;
}

export interface PnlSummaryRow {
  /** Sum of total_cents (incl tax). For ledger reconciliation. */
  gross_revenue_cents: number;
  /** Sales tax — pass-through liability, not income. */
  tax_collected_cents: number;
  /** Revenue NET OF TAX, gross of refunds. */
  revenue_total_cents: number;
  revenue_clients_cents: number;
  revenue_wholesalers_cents: number;
  /** Refunds bucketed on `orders.payments.refunded_at`. Contra-revenue. */
  refunds_cents: number;
  refunds_clients_cents: number;
  refunds_wholesalers_cents: number;
  /** revenue_total - refunds. */
  net_revenue_cents: number;
  cogs_cents: number;
  /** net_revenue - cogs. */
  gross_profit_cents: number;
  commissions_cents: number;
  shipping_cents: number;
  sms_cents: number;
  expenses_total_cents: number;
  /** gross_profit - expenses. */
  net_income_cents: number;
  order_count: number;
}

export interface PnlByStoreRow {
  store_id: string | null;
  store_name: string;
  gross_revenue_cents: number;
  tax_collected_cents: number;
  revenue_total_cents: number;
  revenue_clients_cents: number;
  revenue_wholesalers_cents: number;
  refunds_cents: number;
  net_revenue_cents: number;
  cogs_cents: number;
  gross_profit_cents: number;
  expenses_total_cents: number;
  net_income_cents: number;
  order_count: number;
}

export interface IncomeTimeseriesRow {
  bucket_start: string;
  channel: ReportsChannel;
  revenue_cents: number;
  order_count: number;
}

export interface ExpenseBreakdownRow {
  bucket: ReportsExpenseKind;
  amount_cents: number;
}

export interface RevenueTransactionRow {
  id: string;
  occurred_at: string;
  basis: ReportsBasis;
  channel: ReportsChannel;
  store_id: string;
  store_name: string;
  order_id: string;
  order_number: string;
  client_id: string | null;
  client_name: string | null;
  wholesaler_id: string | null;
  /** Gross paid / invoiced (incl tax). */
  amount_cents: number;
  /** Sales tax portion (allocated proportionally for cash basis). */
  tax_cents: number;
  /** Revenue net of tax. */
  net_cents: number;
  currency: string;
  status: string;
}

export interface RefundTransactionRow {
  id: string;
  refunded_at: string;
  channel: ReportsChannel;
  store_id: string;
  store_name: string;
  order_id: string;
  order_number: string;
  client_id: string | null;
  client_name: string | null;
  wholesaler_id: string | null;
  paid_at: string;
  /** Gross refunded (incl tax). */
  amount_cents: number;
  /** Sales tax allocated to the refund. */
  tax_cents: number;
  /** Refund net of tax. Subtract from net revenue to get net P&L revenue. */
  net_cents: number;
  currency: string;
}

export interface ExpenseTransactionRow {
  id: string;
  occurred_at: string;
  kind: ReportsExpenseKind;
  store_id: string | null;
  store_name: string | null;
  reference_id: string | null;
  description: string;
  amount_cents: number;
  status: string;
}

export interface ListRevenueTransactionsInput extends KeysetInput, ReportsScopeInput {
  channel?: ReportsChannel | null;
}

export interface ListRefundTransactionsInput extends KeysetInput, ReportsRangeInput {
  storeId?: string | null;
  channel?: ReportsChannel | null;
}

export interface ListExpenseTransactionsInput extends KeysetInput, ReportsRangeInput {
  storeId?: string | null;
  kind?: ReportsExpenseKind | null;
}

// ---- helpers ------------------------------------------------------------

function client() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return supabase() as any;
}

function nullableTs(value: string | null | undefined): string | null {
  return value && value.length > 0 ? value : null;
}

// =========================================================================
// 1. P&L summary
// =========================================================================

export async function getPnlSummary(
  ctx: ActiveContext,
  input: ReportsScopeInput = {},
): Promise<PnlSummaryRow> {
  const { orgId } = requireOrgContext(ctx);
  const storeId = input.storeId ?? ctx.storeId ?? null;
  const { data, error } = await client()
    .schema('reports')
    .rpc('fn_pnl_summary', {
      p_org_id: orgId,
      p_store_id: storeId,
      p_from: nullableTs(input.from),
      p_to: nullableTs(input.to),
      p_basis: input.basis ?? 'accrual',
    });
  if (error) throw error;
  const rows = (data ?? []) as PnlSummaryRow[];
  return rows[0] ?? emptyPnlSummary();
}

export function emptyPnlSummary(): PnlSummaryRow {
  return {
    gross_revenue_cents: 0,
    tax_collected_cents: 0,
    revenue_total_cents: 0,
    revenue_clients_cents: 0,
    revenue_wholesalers_cents: 0,
    refunds_cents: 0,
    refunds_clients_cents: 0,
    refunds_wholesalers_cents: 0,
    net_revenue_cents: 0,
    cogs_cents: 0,
    gross_profit_cents: 0,
    commissions_cents: 0,
    shipping_cents: 0,
    sms_cents: 0,
    expenses_total_cents: 0,
    net_income_cents: 0,
    order_count: 0,
  };
}

// =========================================================================
// 2. Per-store P&L (with All-stores roll-up)
// =========================================================================

export async function getPnlByStore(
  ctx: ActiveContext,
  input: ReportsRangeInput & { basis?: ReportsBasis } = {},
): Promise<PnlByStoreRow[]> {
  const { orgId } = requireOrgContext(ctx);
  const { data, error } = await client()
    .schema('reports')
    .rpc('fn_pnl_by_store', {
      p_org_id: orgId,
      p_from: nullableTs(input.from),
      p_to: nullableTs(input.to),
      p_basis: input.basis ?? 'accrual',
    });
  if (error) throw error;
  return (data ?? []) as PnlByStoreRow[];
}

// =========================================================================
// 3. Income timeseries
// =========================================================================

export interface IncomeTimeseriesInput extends ReportsScopeInput {
  grain?: ReportsTimeseriesGrain;
}

export async function getIncomeTimeseries(
  ctx: ActiveContext,
  input: IncomeTimeseriesInput = {},
): Promise<IncomeTimeseriesRow[]> {
  const { orgId } = requireOrgContext(ctx);
  const storeId = input.storeId ?? ctx.storeId ?? null;
  const { data, error } = await client()
    .schema('reports')
    .rpc('fn_income_timeseries', {
      p_org_id: orgId,
      p_store_id: storeId,
      p_from: nullableTs(input.from),
      p_to: nullableTs(input.to),
      p_basis: input.basis ?? 'accrual',
      p_grain: input.grain ?? 'day',
    });
  if (error) throw error;
  return (data ?? []) as IncomeTimeseriesRow[];
}

// =========================================================================
// 4. Expense breakdown
// =========================================================================

export async function getExpenseBreakdown(
  ctx: ActiveContext,
  input: ReportsRangeInput & { storeId?: string | null } = {},
): Promise<ExpenseBreakdownRow[]> {
  const { orgId } = requireOrgContext(ctx);
  const storeId = input.storeId ?? ctx.storeId ?? null;
  const { data, error } = await client()
    .schema('reports')
    .rpc('fn_expense_breakdown', {
      p_org_id: orgId,
      p_store_id: storeId,
      p_from: nullableTs(input.from),
      p_to: nullableTs(input.to),
    });
  if (error) throw error;
  return (data ?? []) as ExpenseBreakdownRow[];
}

// =========================================================================
// 5. Revenue transactions (keyset paginated drill-down)
// =========================================================================

export async function listRevenueTransactions(
  ctx: ActiveContext,
  input: ListRevenueTransactionsInput = {},
): Promise<ListResult<RevenueTransactionRow>> {
  const { orgId } = requireOrgContext(ctx);
  const storeId = input.storeId ?? ctx.storeId ?? null;
  const pageSize = clampPageSize(input.pageSize);
  const { data, error } = await client()
    .schema('reports')
    .rpc('rpc_list_revenue_transactions', {
      p_org_id: orgId,
      p_store_id: storeId,
      p_from: nullableTs(input.from),
      p_to: nullableTs(input.to),
      p_basis: input.basis ?? 'accrual',
      p_channel: input.channel ?? null,
      p_cursor: input.cursor ?? null,
      p_page_size: pageSize,
    });
  if (error) throw error;
  const rows = (data ?? []) as RevenueTransactionRow[];
  return {
    rows,
    nextCursor: buildNextCursor(
      rows as unknown as Array<Record<string, unknown>>,
      pageSize,
      'occurred_at',
    ),
    total: null,
    totalIsExact: false,
  };
}

// =========================================================================
// 6. Refund transactions (keyset paginated drill-down)
// =========================================================================

export async function listRefundTransactions(
  ctx: ActiveContext,
  input: ListRefundTransactionsInput = {},
): Promise<ListResult<RefundTransactionRow>> {
  const { orgId } = requireOrgContext(ctx);
  const storeId = input.storeId ?? ctx.storeId ?? null;
  const pageSize = clampPageSize(input.pageSize);
  const { data, error } = await client()
    .schema('reports')
    .rpc('rpc_list_refund_transactions', {
      p_org_id: orgId,
      p_store_id: storeId,
      p_from: nullableTs(input.from),
      p_to: nullableTs(input.to),
      p_channel: input.channel ?? null,
      p_cursor: input.cursor ?? null,
      p_page_size: pageSize,
    });
  if (error) throw error;
  const rows = (data ?? []) as RefundTransactionRow[];
  return {
    rows,
    nextCursor: buildNextCursor(
      rows as unknown as Array<Record<string, unknown>>,
      pageSize,
      'refunded_at',
    ),
    total: null,
    totalIsExact: false,
  };
}

// =========================================================================
// 7. Expense transactions (keyset paginated drill-down)
// =========================================================================

export async function listExpenseTransactions(
  ctx: ActiveContext,
  input: ListExpenseTransactionsInput = {},
): Promise<ListResult<ExpenseTransactionRow>> {
  const { orgId } = requireOrgContext(ctx);
  const storeId = input.storeId ?? ctx.storeId ?? null;
  const pageSize = clampPageSize(input.pageSize);
  const { data, error } = await client()
    .schema('reports')
    .rpc('rpc_list_expense_transactions', {
      p_org_id: orgId,
      p_store_id: storeId,
      p_from: nullableTs(input.from),
      p_to: nullableTs(input.to),
      p_kind: input.kind ?? null,
      p_cursor: input.cursor ?? null,
      p_page_size: pageSize,
    });
  if (error) throw error;
  const rows = (data ?? []) as ExpenseTransactionRow[];
  return {
    rows,
    nextCursor: buildNextCursor(
      rows as unknown as Array<Record<string, unknown>>,
      pageSize,
      'occurred_at',
    ),
    total: null,
    totalIsExact: false,
  };
}
